import { useCallback, useEffect, useSyncExternalStore } from 'react';
import type { EpgNowNext, EpgProgram, LiveChannel, LiveChannelListResponse } from '@lumos/contracts';
import { formatClock } from '../components/clock.js';
import { getChannelSchedule, getChannelsNowNext, getLiveChannels } from '../api/endpoints.js';
import { nowNextStale } from '../pages/epg-now.js';

/**
 * Данные эфира, общие для страницы каналов и панели каналов в плеере.
 *
 * Зачем общие. Плеер открывается заново на каждый канал (новая сессия — новый
 * экран), и раньше каждый раз тянул весь плейлист — у Skaz под две тысячи
 * каналов — ради одного логотипа. Телепрограмма тоже спрашивалась на странице,
 * а потом ещё раз в панели плеера. Здесь всё это живёт один раз на вкладку.
 */

/* ---------- Список каналов ---------- */

/** Сколько считаем список свежим. Плейлист провайдера меняется редко. */
const CHANNELS_TTL_MS = 5 * 60_000;

let channelsCache: { at: number; response: LiveChannelListResponse } | null = null;
let channelsInFlight: Promise<LiveChannelListResponse> | null = null;

/**
 * Список каналов: из памяти, если он свежий, иначе с сервера. `fresh` — не
 * верить памяти (страница каналов при открытии: зритель пришёл посмотреть
 * актуальное, а сервер отдаёт его из своего кэша сразу).
 */
export function loadLiveChannels(options: { fresh?: boolean; signal?: AbortSignal } = {}): Promise<LiveChannelListResponse> {
  if (!options.fresh && channelsCache !== null && Date.now() - channelsCache.at < CHANNELS_TTL_MS) {
    return Promise.resolve(channelsCache.response);
  }
  if (channelsInFlight === null) {
    channelsInFlight = getLiveChannels()
      .then((response) => {
        channelsCache = { at: Date.now(), response };
        return response;
      })
      .finally(() => {
        channelsInFlight = null;
      });
  }
  const shared = channelsInFlight;
  const signal = options.signal;
  if (signal === undefined) return shared;
  // Общий запрос не отменяем из-за одного ушедшего экрана: он нужен и другим.
  return new Promise((resolve, reject) => {
    if (signal.aborted) {
      reject(new DOMException('Aborted', 'AbortError'));
      return;
    }
    signal.addEventListener('abort', () => reject(new DOMException('Aborted', 'AbortError')), { once: true });
    shared.then(resolve, reject);
  });
}

/** Отметка избранного поменялась — правим и память, чтобы плеер видел то же. */
export function rememberFavorite(channelId: string, favorite: boolean): void {
  if (channelsCache === null) return;
  const channels = channelsCache.response.channels.map((entry) =>
    entry.id === channelId ? { ...entry, favorite } : entry,
  );
  channelsCache = { ...channelsCache, response: { ...channelsCache.response, channels } };
}

/* ---------- Часы ---------- */

/**
 * Общие часы эфира: одна подписка на вкладку, тик раз в `TICK_MS`.
 *
 * Подписываются на них сами карточки (через {@link useWallClock}), а не список:
 * тик перерисовывает только видимые карточки с полосой эфира, а не страницу с
 * плитками на две тысячи каналов.
 */
const TICK_MS = 30_000;
let clockNow = Date.now();
const clockListeners = new Set<() => void>();
let clockTimer: number | null = null;

function subscribeClock(listener: () => void): () => void {
  clockListeners.add(listener);
  if (clockTimer === null) {
    clockNow = Date.now();
    clockTimer = window.setInterval(() => {
      clockNow = Date.now();
      clockListeners.forEach((fn) => fn());
    }, TICK_MS);
  }
  return () => {
    clockListeners.delete(listener);
    if (clockListeners.size === 0 && clockTimer !== null) {
      window.clearInterval(clockTimer);
      clockTimer = null;
    }
  };
}

/** Настенное время с точностью до тика: для «сейчас/далее» и полосы эфира. */
export function useWallClock(): number {
  return useSyncExternalStore(subscribeClock, () => clockNow, () => clockNow);
}

/* ---------- «Сейчас/далее» ---------- */

/** Не чаще этого спрашиваем один и тот же канал повторно. */
const REASK_MS = 60_000;
/**
 * Сколько каналов в одном запросе. Сервер отдаёт из кэша сразу, а незнакомые
 * дозагружает из источника по горстке: просить разом сотни бессмысленно.
 */
const BATCH = 60;
/**
 * Пауза перед отправкой пакета. За неё успевают смонтироваться все карточки
 * экрана (запрос один, а не тридцать), а при быстрой прокрутке — уйти те,
 * мимо которых пролистали (их не спрашиваем вовсе).
 */
const FLUSH_MS = 120;

const nowNextById = new Map<string, EpgNowNext>();
const askedAt = new Map<string, number>();
const inFlight = new Set<string>();
/** Кто сейчас показывает канал: спрашиваем только о видимых. */
const wanted = new Map<string, number>();
const nowNextListeners = new Map<string, Set<() => void>>();
let flushTimer: number | null = null;

function notifyNowNext(id: string): void {
  nowNextListeners.get(id)?.forEach((fn) => fn());
}

function needsAsk(id: string, now: number): boolean {
  if (inFlight.has(id)) return false;
  const known = nowNextById.get(id);
  if (known !== undefined && !nowNextStale(known, now)) return false;
  return now - (askedAt.get(id) ?? 0) >= REASK_MS;
}

function scheduleFlush(): void {
  if (flushTimer !== null) return;
  flushTimer = window.setTimeout(flush, FLUSH_MS);
}

function flush(): void {
  flushTimer = null;
  const now = Date.now();
  const ids = [...wanted.keys()].filter((id) => needsAsk(id, now));
  for (let at = 0; at < ids.length; at += BATCH) {
    const chunk = ids.slice(at, at + BATCH);
    for (const id of chunk) {
      inFlight.add(id);
      askedAt.set(id, now);
    }
    getChannelsNowNext(chunk)
      .then((response) => {
        for (const [id, entry] of Object.entries(response.channels)) {
          nowNextById.set(id, entry);
          notifyNowNext(id);
        }
      })
      .catch(() => undefined)
      .finally(() => {
        // Отметка «в пути» — про запрос, а не про канал навсегда: незнакомые
        // каналы сервер дозагружает по горстке, и через минуту их спросим снова.
        for (const id of chunk) inFlight.delete(id);
      });
  }
}

/**
 * «Сейчас/далее» одного канала. Карточка, смонтированная на экране, сама
 * заявляет интерес; запросы собираются в пакет `/livetv/epg/now-next`.
 * Перечитывается то, что кончилось (по часам), — но не чаще раза в минуту.
 */
export function useNowNext(epgId: string | null, wallClock: number): EpgNowNext | null {
  const subscribe = useCallback(
    (listener: () => void) => {
      if (epgId === null) return () => undefined;
      let set = nowNextListeners.get(epgId);
      if (set === undefined) {
        set = new Set();
        nowNextListeners.set(epgId, set);
      }
      set.add(listener);
      return () => {
        set.delete(listener);
        if (set.size === 0) nowNextListeners.delete(epgId);
      };
    },
    [epgId],
  );
  const value = useSyncExternalStore(
    subscribe,
    () => (epgId === null ? null : (nowNextById.get(epgId) ?? null)),
    () => null,
  );
  useEffect(() => {
    if (epgId === null) return;
    wanted.set(epgId, (wanted.get(epgId) ?? 0) + 1);
    return () => {
      const left = (wanted.get(epgId) ?? 1) - 1;
      if (left <= 0) wanted.delete(epgId);
      else wanted.set(epgId, left);
    };
  }, [epgId]);
  useEffect(() => {
    if (epgId !== null && needsAsk(epgId, wallClock)) scheduleFlush();
  }, [epgId, wallClock, value]);
  return value;
}

/* ---------- Расписание канала ---------- */

const SCHEDULE_TTL_MS = 10 * 60_000;
const schedules = new Map<string, { at: number; programs: EpgProgram[] }>();

/** Полное расписание канала (с описаниями). Из памяти, если свежее. */
export async function loadSchedule(epgId: string, signal?: AbortSignal): Promise<EpgProgram[]> {
  const cached = schedules.get(epgId);
  const now = Date.now();
  // Кончилась последняя известная передача — расписание устарело раньше срока.
  const last = cached?.programs[cached.programs.length - 1];
  if (cached !== undefined && now - cached.at < SCHEDULE_TTL_MS && (last === undefined || last.stop > now)) {
    return cached.programs;
  }
  const response = await getChannelSchedule(epgId, signal);
  const programs = [...response.programs].sort((a, b) => a.start - b.start);
  schedules.set(epgId, { at: Date.now(), programs });
  return programs;
}

/** Известное расписание без запроса — чтобы не мигать заглушкой при возврате. */
export function cachedSchedule(epgId: string): EpgProgram[] | null {
  return schedules.get(epgId)?.programs ?? null;
}

/**
 * Ближайшие передачи: идущая сейчас и следующие за ней, всего `count`.
 * Нет идущей (дыра в расписании) — ближайшие предстоящие.
 */
export function upcomingPrograms(programs: readonly EpgProgram[], now: number, count: number): EpgProgram[] {
  const from = programs.findIndex((program) => program.stop > now);
  if (from < 0) return [];
  return programs.slice(from, from + count);
}

/* ---------- Подписи ---------- */

/** Время передачи в 24-часовом формате по часам зрителя. */
export function clockLabel(epochMs: number): string {
  return formatClock(epochMs);
}

/** Доля прошедшего (0–1) или `null`, если передача без длительности. */
export function airedShare(program: EpgProgram, now: number): number | null {
  const span = program.stop - program.start;
  if (span <= 0) return null;
  return Math.max(0, Math.min(1, (now - program.start) / span));
}

/** «осталось 22 мин» / «осталось 1 ч 05 мин». */
export function leftLabel(program: EpgProgram, now: number): string {
  const minutes = Math.max(1, Math.round((program.stop - now) / 60_000));
  if (minutes < 60) return `осталось ${minutes} мин`;
  const hours = Math.floor(minutes / 60);
  const rest = minutes % 60;
  return rest === 0 ? `осталось ${hours} ч` : `осталось ${hours} ч ${String(rest).padStart(2, '0')} мин`;
}

/* ---------- Группы ---------- */

/** Что показывает список: избранное, все каналы или одна группа плейлиста. */
export type ChannelView = { kind: 'all' } | { kind: 'favorites' } | { kind: 'group'; group: string };

export function sameView(a: ChannelView, b: ChannelView): boolean {
  if (a.kind !== b.kind) return false;
  return a.kind !== 'group' || (b.kind === 'group' && a.group === b.group);
}

/**
 * Группы плейлиста в порядке появления (а не по алфавиту): плейлист уже
 * упорядочен осмысленно — «Федеральные» первыми.
 */
export function channelGroups(channels: readonly LiveChannel[]): string[] {
  const found = new Set<string>();
  for (const channel of channels) {
    if (channel.group !== null && channel.group !== '') found.add(channel.group);
  }
  return [...found];
}

export function channelsInView(channels: readonly LiveChannel[], view: ChannelView): LiveChannel[] {
  if (view.kind === 'all') return [...channels];
  if (view.kind === 'favorites') return channels.filter((channel) => channel.favorite);
  return channels.filter((channel) => channel.group === view.group);
}

export function viewLabel(view: ChannelView): string {
  return view.kind === 'all' ? 'Все' : view.kind === 'favorites' ? 'Избранное' : view.group;
}

export function viewKey(view: ChannelView): string {
  return view.kind === 'group' ? `g:${view.group}` : view.kind;
}
